import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Download, FileText, Table as TableIcon, FileSpreadsheet, Loader2, CheckCircle2, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { buildCsvContent, buildPdfHtml, buildExcelWorkbook } from './export/exportHelpers';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function formatWindow(fromIso, toIso) {
  if (!fromIso || !toIso) return '—';
  try {
    const fmt = (iso) => {
      const d = new Date(iso + 'T00:00:00Z');
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
    };
    return `${fmt(fromIso)} → ${fmt(toIso)}`;
  } catch {
    return `${fromIso} → ${toIso}`;
  }
}

const TaxDataPackSubsection = ({ taxPackPreview, taxPackLoading, taxPackDownloading, downloadTaxPack, onGoToSettings }) => (
  <div className="mt-6 pt-6 border-t border-slate-200" data-testid="exports-tax-data-pack-subsection">
    <div className="flex items-start gap-2">
      <Receipt className="w-4 h-4 text-emerald-700 mt-1 shrink-0" />
      <div className="flex-1">
        <h3 className="font-semibold text-slate-900 text-sm">For your accountant — Tax Data Pack</h3>
        <p className="text-xs text-slate-500 mt-1">
          A country-agnostic record sized to your tax year: holdings as of period end, income events
          (bond coupons, T-bill and FD interest), and cycles completed within the period. Native
          currency only — your accountant applies the right FX rate.
        </p>
      </div>
    </div>

    <div
      className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs"
      data-testid="exports-tax-data-pack-window"
    >
      {taxPackLoading ? (
        <span className="flex items-center text-slate-500">
          <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Computing window…
        </span>
      ) : taxPackPreview ? (
        <>
          <span className="text-slate-700">
            <span className="uppercase tracking-wide text-[10px] text-slate-500">Tax year</span>{' '}
            <span className="font-medium">{formatWindow(taxPackPreview.from_date, taxPackPreview.to_date)}</span>
          </span>
          <span className="text-slate-600">
            {taxPackPreview.summary?.holdings_count ?? 0} holding{(taxPackPreview.summary?.holdings_count ?? 0) === 1 ? '' : 's'}
            {' · '}{taxPackPreview.summary?.income_event_count ?? 0} income event{(taxPackPreview.summary?.income_event_count ?? 0) === 1 ? '' : 's'}
            {' · '}{taxPackPreview.summary?.closed_position_count ?? 0} closed
          </span>
        </>
      ) : (
        <span className="text-slate-500">Tax year preview unavailable.</span>
      )}
    </div>

    <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="exports-tax-data-pack-downloads">
      <Button variant="outline" size="sm" disabled={taxPackDownloading !== null || taxPackLoading} onClick={() => downloadTaxPack('csv')} className="justify-start border-slate-300 hover:border-emerald-500 hover:text-emerald-800" data-testid="exports-tax-data-pack-download-csv">
        {taxPackDownloading === 'csv' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TableIcon className="w-4 h-4 mr-2 text-emerald-700" />}
        Tax Pack CSV
      </Button>
      <Button variant="outline" size="sm" disabled={taxPackDownloading !== null || taxPackLoading} onClick={() => downloadTaxPack('xlsx')} className="justify-start border-slate-300 hover:border-emerald-500 hover:text-emerald-800" data-testid="exports-tax-data-pack-download-xlsx">
        {taxPackDownloading === 'xlsx' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-700" />}
        Tax Pack Excel
      </Button>
      <Button variant="outline" size="sm" disabled={taxPackDownloading !== null || taxPackLoading} onClick={() => downloadTaxPack('pdf')} className="justify-start border-slate-300 hover:border-emerald-500 hover:text-emerald-800" data-testid="exports-tax-data-pack-download-pdf">
        {taxPackDownloading === 'pdf' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2 text-emerald-700" />}
        Tax Pack PDF
      </Button>
    </div>

    {onGoToSettings && (
      <p className="mt-2 text-xs text-slate-500" data-testid="exports-tax-data-pack-settings-link">
        Change tax-year start date in{' '}
        <button type="button" onClick={onGoToSettings} className="text-emerald-700 hover:text-emerald-900 underline underline-offset-2" data-testid="exports-tax-data-pack-go-to-settings">
          Settings → Tax Data Pack
        </button>.
      </p>
    )}
  </div>
);

const ExportReports = ({ investments, portfolioSummary, onGoToSettings }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [taxPackPreview, setTaxPackPreview] = useState(null);
  const [taxPackLoading, setTaxPackLoading] = useState(true);
  const [taxPackDownloading, setTaxPackDownloading] = useState(null); // 'csv' | 'xlsx' | 'pdf' | null

  // Fetch the Tax Data Pack preview once so the subsection can surface a live
  // window + row count. Stays passive — failures degrade silently.
  useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/tax-data-pack/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({}),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setTaxPackPreview(d); })
      .catch(() => { if (!cancelled) setTaxPackPreview(null); })
      .finally(() => { if (!cancelled) setTaxPackLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const downloadTaxPack = async (fmt) => {
    setTaxPackDownloading(fmt);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/tax-data-pack/export?fmt=${fmt}`, {
        headers: authHeaders(),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `Download failed (${resp.status})`);
      }
      const blob = await resp.blob();
      const cd = resp.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="?([^";]+)"?/i);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = m ? m[1] : `myinvestfolio-tax-data-pack.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Tax Data Pack ${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e.message || 'Download failed');
    } finally {
      setTaxPackDownloading(null);
    }
  };

  const exportToCSV = () => {
    try {
      setIsExporting(true);
      if (!investments || investments.length === 0) { toast.error('No investments to export'); return; }
      const csv = buildCsvContent(investments, portfolioSummary);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `myinvestfolio_investments_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('CSV exported successfully!');
    } catch (error) {
      logger.error('Export error:', error);
      toast.error('Failed to export CSV');
    } finally {
      setIsExporting(false);
    }
  };

  const exportToExcel = () => {
    try {
      setIsExporting(true);
      if (!investments || investments.length === 0) { toast.error('No investments to export'); return; }
      const wbOut = buildExcelWorkbook(investments, portfolioSummary);
      const blob = new Blob([wbOut], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `myinvestfolio_investments_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Excel exported successfully!');
    } catch (error) {
      logger.error('Excel export error:', error);
      toast.error('Failed to export Excel');
    } finally {
      setIsExporting(false);
    }
  };

  const exportToPDF = () => {
    try {
      setIsExporting(true);
      const printWindow = window.open('', '_blank');
      if (!printWindow) { toast.error('Please allow popups to export PDF'); return; }
      const html = buildPdfHtml(investments, portfolioSummary);
      printWindow.document.write(html);
      printWindow.document.close();
      toast.success('Opening print dialog...');
    } catch (error) {
      logger.error('PDF export error:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Download className="w-5 h-5" />
          <span>Export & Reports</span>
        </CardTitle>
        <CardDescription>Download your portfolio data in various formats</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border rounded-lg p-4 hover:border-green-300 transition-colors">
            <div className="flex flex-col items-center text-center space-y-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <TableIcon className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="font-semibold text-slate-900 text-sm">CSV Export</h3>
              <p className="text-xs text-slate-500">For Google Sheets and data analysis</p>
              <Button data-testid="export-csv-btn" onClick={exportToCSV} disabled={isExporting || !investments || investments.length === 0} className="w-full" variant="outline" size="sm">
                {isExporting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Exporting...</>) : (<><Download className="w-4 h-4 mr-2" />Export CSV</>)}
              </Button>
            </div>
          </div>
          <div className="border rounded-lg p-4 hover:border-emerald-300 transition-colors">
            <div className="flex flex-col items-center text-center space-y-2">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-slate-900 text-sm">Excel Export</h3>
              <p className="text-xs text-slate-500">For Microsoft Excel with formatting</p>
              <Button data-testid="export-excel-btn" onClick={exportToExcel} disabled={isExporting || !investments || investments.length === 0} className="w-full" variant="outline" size="sm">
                {isExporting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Exporting...</>) : (<><Download className="w-4 h-4 mr-2" />Export Excel</>)}
              </Button>
            </div>
          </div>
          <div className="border rounded-lg p-4 hover:border-blue-300 transition-colors">
            <div className="flex flex-col items-center text-center space-y-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="font-semibold text-slate-900 text-sm">PDF Report</h3>
              <p className="text-xs text-slate-500">Professional portfolio report</p>
              <Button data-testid="export-pdf-btn" onClick={exportToPDF} disabled={isExporting || !investments || investments.length === 0} className="w-full" variant="outline" size="sm">
                {isExporting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>) : (<><FileText className="w-4 h-4 mr-2" />Generate PDF</>)}
              </Button>
            </div>
          </div>
        </div>
        <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5" />
            <div className="text-xs text-slate-600">
              <p className="font-semibold mb-1">What&apos;s included:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>All investment details (name, type, amounts)</li>
                <li>Performance data (profit/loss, returns)</li>
                <li>Portfolio summary and totals</li>
                <li>Generated timestamp for record-keeping</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Tax Data Pack — accountant-focused subsection */}
        <TaxDataPackSubsection
          taxPackPreview={taxPackPreview}
          taxPackLoading={taxPackLoading}
          taxPackDownloading={taxPackDownloading}
          downloadTaxPack={downloadTaxPack}
          onGoToSettings={onGoToSettings}
        />
      </CardContent>
    </Card>
  );
};

export default ExportReports;
